interface CodedError {
  code?: number;
}

export enum ErrorCode {
  THRESHOLD_LIMIT = 6900,
  AI_THRESHOLD_LIMIT = 4253,
  VOICE_AI_TEMPORARILY_UNAVAILABLE = 4262,
  REQUESTS_LIMIT = 4303,
}

export class ThresholdLimitError extends Error {
  code: number;

  constructor() {
    super('Threshold limit reached');
    this.name = 'ThresholdLimitError';
    this.code = ErrorCode.THRESHOLD_LIMIT;
  }
}

export class AiThresholdLimitError extends Error {
  code: number;

  constructor() {
    super('AI threshold limit reached');
    this.name = 'AiThresholdLimitError';
    this.code = ErrorCode.AI_THRESHOLD_LIMIT;
  }
}

export class RequestLimitError extends Error {
  code: number;

  constructor() {
    super('Active requests limit reached');
    this.name = 'RequestLimitError';
    this.code = ErrorCode.REQUESTS_LIMIT;
  }
}

export const isThresholdLimitError = (error: Error): error is ThresholdLimitError => {
  return (
    error instanceof ThresholdLimitError ||
    (typeof (error as CodedError).code === 'number' && (error as CodedError).code === ErrorCode.THRESHOLD_LIMIT)
  );
};

export const isAiThresholdLimitError = (error: Error): error is AiThresholdLimitError => {
  return (
    error instanceof AiThresholdLimitError ||
    (typeof (error as CodedError).code === 'number' && (error as CodedError).code === ErrorCode.AI_THRESHOLD_LIMIT)
  );
};

export const isRequestLimitError = (error: Error): error is RequestLimitError => {
  return (
    error instanceof RequestLimitError ||
    (typeof (error as CodedError).code === 'number' && (error as CodedError).code === ErrorCode.REQUESTS_LIMIT)
  );
};

export const parseErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
};

export const getApiErrorCode = (error: unknown): number | undefined => {
  if (typeof (error as CodedError)?.code === 'number') {
    return (error as CodedError).code;
  }

  return undefined;
};

/**
 * Business-rule outcomes with dedicated UX (limit modals) — expected, not incidents.
 */
const EXPECTED_BUSINESS_ERROR_CODES: ReadonlySet<number> = new Set([
  ErrorCode.THRESHOLD_LIMIT,
  ErrorCode.AI_THRESHOLD_LIMIT,
  ErrorCode.REQUESTS_LIMIT,
]);

export const isExpectedBusinessErrorCode = (code: unknown): boolean => {
  const numericCode = typeof code === 'string' ? Number(code) : code;
  if (typeof numericCode !== 'number' || !Number.isFinite(numericCode)) return false;
  return EXPECTED_BUSINESS_ERROR_CODES.has(numericCode);
};

export const isExpectedBusinessError = (error: unknown): boolean => isExpectedBusinessErrorCode(getApiErrorCode(error));
